import {
  Animations,
  Button,
  ModalController,
  Spinners,
  useForm,
  useToast,
  Validators,
} from '@keymax-dev/smartepi-ui';
import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import PageTitle from '../../../components/page-title';
import useRest from '../../../hooks/use-rest';
import { Product, Size, ErrorResponse } from '../../../domain/models';
import { EndPoints } from '../../../utils/constants';
import { getErrorMessage } from '../../../utils/error-messages';
import { AddProductModalElement } from './style';

enum Content {
  Form,
  Loading,
}

interface AddProductModalProps {
  controller?: ModalController;
}

const { usePost } = useRest();

const Strings = {
  Buttons: {
    Add: 'Adicionar',
  },
  Inputs: {
    ProductName: 'Nome do Produto',
    Validity: 'Validade (em dias)',
    Certificate: 'Certificado',
  },
  Messages: {
    RegisterLoading: 'Cadastrando produto...',
    RegisterSuccess: 'Produto adicionado com successo!',
    UnknownError:
      'Ocorreu um erro inesperado. Por favor, verifique sua conexão com a internet.',
    InvalidForm: 'Os dados fornecidos são inválidos!',
  },
  Title: 'Adicionar Produto',
};

const DEFAULT_PRODUCT_SIZE: Pick<Size.Data, 'code' | 'size'> = {
  code: '',
  size: 'Único',
};

export default function AddProductModal({
  controller,
}: AddProductModalProps): JSX.Element {
  const [data, post] = usePost({ loading: false });
  const errorToast = useToast(<span />, { color: 'danger' });
  const successToast = useToast(
    <span>{Strings.Messages.RegisterSuccess}</span>,
    { color: 'success' }
  );

  const [form, getErrors, getValues] = useForm(
    [
      {
        key: 'name',
        validators: [new Validators.Required()],
      },
      {
        key: 'useValidity',
        validators: [new Validators.Required()],
      },
      {
        key: 'certificate',
        validators: [],
      },
    ],
    'addProduct'
  );

  const contentAnimation = useAnimation();
  const [content, _setContent] = useState<Content>(Content.Form);
  const setContent = (value: Content): void => {
    contentAnimation
      .start(Animations.FadeOut)
      .then(() => Promise.resolve(_setContent(value)))
      .then(() => contentAnimation.start(Animations.FadeIn));
  };

  const requestSuccessHandler = (data: Product.Data): void => {
    setTimeout(() => successToast.open());
    if (controller) controller.close(data);
  };

  const requestErrorHandler = (error: ErrorResponse): void => {
    errorToast.setContent(getErrorMessage(error));
    setTimeout(() => errorToast.open());
  };

  const confirmHandler = (): void => {
    if (!getErrors()) {
      post(EndPoints.Product(), {
        ...getValues(),
        sizes: [DEFAULT_PRODUCT_SIZE],
      });
    } else {
      errorToast.setContent(Strings.Messages.InvalidForm);
      errorToast.open();
    }
  };

  useEffect(() => {
    if (data.loading) {
      setContent(Content.Loading);
    } else {
      setContent(Content.Form);
      if (data.payload) requestSuccessHandler(data.payload.data);
      else if (data.error) requestErrorHandler(data.error);
    }
  }, [data]);

  return (
    <AddProductModalElement>
      <PageTitle>{Strings.Title}</PageTitle>

      {content === Content.Form && (
        <motion.div className="__form-container">
          <div className="__form-container">
            <form.name placeholder={Strings.Inputs.ProductName} />
            <form.useValidity
              placeholder={Strings.Inputs.Validity}
              type="number"
              min={0}
            />
            <form.certificate placeholder={Strings.Inputs.Certificate} />
          </div>
          <Button
            text={Strings.Buttons.Add}
            color="success"
            onClick={confirmHandler}
          />
        </motion.div>
      )}
      {content === Content.Loading && (
        <motion.div
          className="__form-container"
          animate={contentAnimation}
          initial={{ opacity: 0 }}
        >
          <Spinners.circles width="300px" height="300px" />
          <span>{Strings.Messages.RegisterLoading}</span>
        </motion.div>
      )}
    </AddProductModalElement>
  );
}
